import { Button } from 'base/ui/button';
import { IconSend } from 'base/ui/icons';
import { Textarea } from 'base/ui/textarea';
import { TextSmallLight } from 'base/ui/typography';
import React, { useState } from 'react';
import styles from './feedback_form.css';
import { DividerLine } from './panel';

type FeedbackFormProps = {
  /** This is an unfortunate property.
   * It's used to style the divider which needs to go over the edges
   * to comply with the look & feel of the other panels.
   * - Use `dialog` when the parent is a `<Dialog>`.
   * - Use `panel` when the parent is a sidebar panel (e.g. `<PanelLayout>`). */
  parentVariant: 'dialog' | 'panel';
  onSubmit: (data: { message: string }) => Promise<void>;
};

export const FeedbackForm = ({
  parentVariant,
  onSubmit,
}: FeedbackFormProps) => {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const hasMessage = message.trim().length > 3;

  const handleSubmit = async () => {
    if (!hasMessage) return;

    setSubmitting(true);
    try {
      await onSubmit({ message });
      setMessage('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <TextSmallLight color="grey">
        Any feedback good or bad is welcome! Thanks in advance
      </TextSmallLight>
      <TextSmallLight>
        <Textarea
          value={message}
          onChange={setMessage}
          minHeight={105}
          maxHeight={105}
          placeholder="Feedback"
        />
      </TextSmallLight>
      {parentVariant === 'panel' ? (
        <DividerLine expandToEdges={true} />
      ) : (
        // `<Dialogs>` use less padding than `<PanelLayouts>`.
        // To avoid overflowing outside of the `<Dialog>`
        // we add some extra spacing on the sides.
        <div className={styles.dividerForDialog}>
          <DividerLine expandToEdges={true} />
        </div>
      )}
      <Button
        variant="primary"
        label="Submit"
        icon={IconSend}
        tooltip={
          submitting
            ? 'Submitting'
            : !hasMessage
            ? 'Please write your feedback'
            : undefined
        }
        onClick={handleSubmit}
        loading={submitting}
        disabled={!hasMessage}
      />
    </div>
  );
};
